import { useCallback, useEffect, useMemo, useState } from "react";
import { anomalies, employees, mails, storyEvents } from "./data/generated";
import { startRun, judgeMail, mergeRunIntoSave } from "./game/gameMachine";
import { validateCatalogs } from "./game/validators";
import { clearSave, loadSave, writeSave } from "./services/saveService";
import { playCue } from "./services/audioService";
import type { GameSettings, GameState, SaveData, ScreenId } from "./types";
import { TitleScreen } from "./screens/TitleScreen";
import { PortalScreen } from "./screens/PortalScreen";
import { MailboxScreen } from "./screens/MailboxScreen";
import { DirectoryScreen } from "./screens/DirectoryScreen";
import { ManualScreen } from "./screens/ManualScreen";
import { SettingsScreen } from "./screens/SettingsScreen";
import { EndingScreen } from "./screens/EndingScreen";
import "./styles/global.css";

const validationIssues = validateCatalogs(employees, mails, anomalies, storyEvents);
for (const issue of validationIssues) {
  const method = issue.level === "error" ? console.error : console.warn;
  method(`[data:${issue.source}] ${issue.message}`);
}

export default function App() {
  const [save, setSave] = useState<SaveData>(() => loadSave());
  const [run, setRun] = useState<GameState | undefined>(() => loadSave().currentRun);
  const [completedRun, setCompletedRun] = useState<GameState | undefined>();
  const [screen, setScreen] = useState<ScreenId>("title");
  const [settingsReturn, setSettingsReturn] = useState<ScreenId>("title");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string>();
  const [rewinding, setRewinding] = useState(false);
  const debug = useMemo(() => new URLSearchParams(window.location.search).get("debug") === "1", []);

  useEffect(() => writeSave(save), [save]);

  useEffect(() => {
    document.documentElement.style.setProperty("--ui-scale", String(save.settings.uiScale));
    document.documentElement.dataset.contrast = save.settings.highContrast ? "high" : "normal";
    document.documentElement.dataset.reducedFlash = save.settings.reducedFlash ? "true" : "false";
  }, [save.settings]);

  const persistRun = useCallback((nextRun: GameState) => {
    setRun(nextRun);
    setSave((current) => ({ ...current, currentRun: nextRun }));
  }, []);

  const newGame = useCallback(() => {
    const nextRun = startRun();
    setCompletedRun(undefined);
    persistRun(nextRun);
    setScreen("portal");
    setFeedback(undefined);
    playCue("mail", save.settings);
  }, [persistRun, save.settings]);

  const continueGame = useCallback(() => {
    if (!save.currentRun) return;
    setRun(save.currentRun);
    setScreen("portal");
  }, [save.currentRun]);

  const openSettings = useCallback((from: ScreenId) => {
    setSettingsReturn(from);
    setScreen("settings");
  }, []);

  const handleJudge = useCallback((selectedAnomaly: boolean) => {
    if (!run || busy) return;
    setBusy(true);
    playCue("judge", save.settings);
    const result = judgeMail(run, selectedAnomaly);

    if (result.reachedEnding) {
      const merged = mergeRunIntoSave(save, result.state);
      setSave(merged);
      setRun(undefined);
      setCompletedRun(result.state);
      setFeedback(undefined);
      setScreen("ending");
      playCue("final", save.settings);
      setBusy(false);
      return;
    }

    persistRun(result.state);
    if (result.enteredLoop) {
      setFeedback("監視記録との不一致を検出。23:40へ再同期します。");
      setRewinding(true);
      playCue("rewind", save.settings);
      window.setTimeout(() => setRewinding(false), save.settings.reducedFlash ? 200 : 850);
    } else if (result.reachedFinal) {
      setFeedback("連続監視記録を確認。最終処理メールを受信しました。");
      playCue("final", save.settings);
    } else {
      setFeedback("処理記録を更新しました。次のメールを確認してください。");
      playCue("mail", save.settings);
    }
    window.setTimeout(() => setFeedback(undefined), 2600);
    window.setTimeout(() => setBusy(false), 180);
  }, [busy, persistRun, run, save]);

  const updateSettings = useCallback((settings: GameSettings) => {
    setSave((current) => ({ ...current, settings }));
  }, []);

  const resetAll = useCallback(() => {
    const reset = clearSave();
    setSave(reset);
    setRun(undefined);
    setCompletedRun(undefined);
    setScreen("title");
  }, []);

  let content;
  if (screen === "title") {
    content = <TitleScreen canContinue={Boolean(save.currentRun)} profile={save.profile} onNewGame={newGame} onContinue={continueGame} onSettings={() => openSettings("title")} />;
  } else if (screen === "settings") {
    content = <SettingsScreen settings={save.settings} onChange={updateSettings} onResetSave={resetAll} onBack={() => setScreen(settingsReturn)} />;
  } else if (screen === "ending" && completedRun) {
    content = <EndingScreen run={completedRun} profile={save.profile} onReplay={newGame} onTitle={() => setScreen("title")} />;
  } else if (!run) {
    content = <TitleScreen canContinue={Boolean(save.currentRun)} profile={save.profile} onNewGame={newGame} onContinue={continueGame} onSettings={() => openSettings("title")} />;
  } else if (screen === "portal") {
    content = <PortalScreen run={run} onOpenMailbox={() => setScreen("mailbox")} onOpenDirectory={() => setScreen("directory")} onOpenManual={() => setScreen("manual")} onOpenSettings={() => openSettings("portal")} onReturnTitle={() => setScreen("title")} />;
  } else if (screen === "directory") {
    content = <DirectoryScreen run={run} onBack={() => setScreen("portal")} />;
  } else if (screen === "manual") {
    content = <ManualScreen run={run} onBack={() => setScreen("portal")} />;
  } else {
    content = <MailboxScreen run={run} busy={busy} feedback={feedback} rewinding={rewinding} debug={debug} onJudge={handleJudge} onPortal={() => setScreen("portal")} onDirectory={() => setScreen("directory")} onManual={() => setScreen("manual")} onSettings={() => openSettings("mailbox")} />;
  }

  return <div className="game-root">{content}</div>;
}
